import React from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext'; // Assuming you have this context

// --- REDESIGNED: Profile Page Component ---
const ProfilePage = () => {
  const { user, updateProfile, changePassword } = useAuth();

  // Form for updating profile information
  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    reset: resetProfile,
    formState: { errors: profileErrors, isSubmitting: isUpdatingProfile },
  } = useForm({
    defaultValues: {
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
    },
  });

  // Form for changing the password
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    reset: resetPassword,
    formState: { errors: passwordErrors, isSubmitting: isChangingPassword },
  } = useForm();

  // Handler for the profile update submission
  const onUpdateProfile = async (data) => {
    try {
      await updateProfile(data);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to update profile.');
    }
  };

  // Handler for the password change submission
  const onChangePassword = async (data) => {
    if (data.new_password.length < 6) {
      return toast.error('New password must be at least 6 characters long.');
    }
    try {
      await changePassword(data);
      toast.success('Password changed successfully!');
      resetPassword(); // Clear password fields after success
    } catch (error) {
      toast.error(error.message || 'Failed to change password.');
    }
  };

  // Pre-fill profile form when user data is available
  React.useEffect(() => {
    if (user) {
      resetProfile({ first_name: user.first_name, last_name: user.last_name });
    }
  }, [user, resetProfile]);

  return (
    <div className="flex flex-col max-w-xl mx-auto space-y-8">
      <div className='flex items-center justify-center'>
        <h1 className='font-bold text-3xl'>Profile</h1>

      </div>
      {/* Profile Information Card */}
      <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
        <form onSubmit={handleSubmitProfile(onUpdateProfile)} className="space-y-6">
          {/* Email Field (Disabled) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={user?.email || 'loading...'}
              disabled
              className="w-full p-3 bg-gray-100 border border-gray-200 rounded-lg text-gray-500"
            />
          </div>

          {/* First and Last Name Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">First name</label>
              <input
                id="first_name"
                type="text"
                className={`w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 ${profileErrors.first_name ? 'border-red-500' : ''}`}
                {...registerProfile('first_name', { required: 'First name is required' })}
              />
            </div>
            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
              <input
                id="last_name"
                type="text"
                className={`w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 ${profileErrors.last_name ? 'border-red-500' : ''}`}
                {...registerProfile('last_name', { required: 'Last name is required' })}
              />
            </div>
          </div>

          {/* Update Button */}
          <div className="pt-2">
            <button type="submit" disabled={isUpdatingProfile} className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
              {isUpdatingProfile ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>
      </div>

      {/* Change Password Card */}
      <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
        <form onSubmit={handleSubmitPassword(onChangePassword)} className="space-y-6">
          {/* Current Password Field */}
          <div>
            <label htmlFor="current_password" className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <input
              id="current_password"
              type="password"
              className={`w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 ${passwordErrors.current_password ? 'border-red-500' : ''}`}
              placeholder="Current Password"
              {...registerPassword('current_password', { required: 'Current password is required' })}
            />
          </div>

          {/* New Password Field */}
          <div>
            <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input
              id="new_password"
              type="password"
              className={`w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 ${passwordErrors.new_password ? 'border-red-500' : ''}`}
              placeholder="New Password"
              {...registerPassword('new_password', { required: 'New password is required' })}
            />
          </div>

          {/* Change Password Button */}
          <div className="pt-2">
            <button type="submit" disabled={isChangingPassword} className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
              {isChangingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;